import { useFeatureIsOn, useGrowthBook } from '@growthbook/growthbook-react'

import { SKIP_STYLE_ADVISOR_SELECTION_FLAG } from '../../config'
import { useBookingStore } from '../../store'

/**
 * What to do with the style-advisor screen.
 * `'deciding'` is its own answer rather than a second boolean beside `'skip'`:
 * a flag reads `false` both before the payload lands and before the attributes
 * it targets have been pushed, so "not yet" and "no" are the same value coming
 * out of GrowthBook and have to be told apart here. A caller that treated
 * `'deciding'` as `'show'` would render the screen and yank it away a tick
 * later.
 */
export type StyleAdvisorScreen = 'skip' | 'show' | 'deciding'

/**
 * Reads `RP-skip-styleadvisor-selection`.
 *
 * Which stores it applies to lives in the flag's own targeting rule, against
 * the `locationCode` attribute `FeatureFlagsProvider` sends. Nothing here knows
 * or should know the store list.
 *
 * One thing outside GrowthBook still overrides it: arriving at the list from
 * the timeslots means the visitor asked for it ("See all style advisors" is the
 * only route in from there), and the flag must not send them back where they
 * came from — so that arrival shows the screen without waiting on anything.
 */
export function useStyleAdvisorScreen(): StyleAdvisorScreen {
  const growthbook = useGrowthBook()
  const isOn = useFeatureIsOn(SKIP_STYLE_ADVISOR_SELECTION_FLAG)
  const locationCode = useBookingStore((state) => state.locationCode)
  const screenHistory = useBookingStore((state) => state.screenHistory)

  const wasRequested = screenHistory[screenHistory.length - 1] === 'timeslots'
  if (wasRequested) return 'show'

  // A rule can only match an attribute GrowthBook has already been given, and
  // the provider pushes attributes from an effect — so on the render where the
  // store first becomes known the client is still a tick behind, and a targeted
  // store reads as untargeted. Compare against what the client actually holds
  // instead of assuming the write has landed.
  const attributes = growthbook.getAttributes()
  const isStoreApplied = attributes.locationCode === (locationCode ?? null)

  // The flag can carry an A/B test that buckets on `id`. Read before the
  // provider has it, the test is skipped and the visitor gets the next rule —
  // then flips variant a tick later. `null` (no consent) is an answer; only a
  // missing key is "not yet", and `getExperimentId` bounds how long that lasts.
  const isIdApplied = 'id' in attributes

  // `ready` covers a failed load too: the SDK catches its own fetch errors and
  // sets an empty payload, so a blocked CDN settles on the flag defaults rather
  // than parking this screen on its loader for good.
  if (!growthbook.ready || !isStoreApplied || !isIdApplied) return 'deciding'

  return isOn ? 'skip' : 'show'
}
